import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Button from '../components/ui/Button';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { wakeSqlWarehouse } from '../services/sqlWarehouseService';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';

/** Drop empty signal fields (nested objects included) for Result JSON (Query page display only). */
function filterSignalValueForDisplay(value) {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value.trim() !== '' ? value : undefined;
  if (Array.isArray(value)) {
    const mapped = value.map(filterSignalValueForDisplay).filter((v) => v !== undefined);
    return mapped.length > 0 ? mapped : undefined;
  }
  if (typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      const v = filterSignalValueForDisplay(value[key]);
      if (v !== undefined) out[key] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }
  return value;
}

function filterSignalRecordForDisplay(record) {
  if (!record || typeof record !== 'object') return record;
  return filterSignalValueForDisplay(record) ?? {};
}

function formatProfileResultForDisplay(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  if (!Array.isArray(data.signal_records)) return data;
  return {
    ...data,
    signal_records: data.signal_records
      .map(filterSignalRecordForDisplay)
      .filter((r) => Object.keys(r).length > 0),
  };
}

const Query = () => {
  const [identifierType, setIdentifierType] = useState('email');
  const [queryInput, setQueryInput] = useState('');
  const [useApiKey, setUseApiKey] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    wakeSqlWarehouse();

    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);

        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some((o) => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };

    loadOrganizations();
  }, []);

  const buildApiKeyHeaders = () => {
    if (!useApiKey) {
      return {};
    }
    const token = apiKeyInput.trim();
    if (!token) {
      return {};
    }
    return { Authorization: `apikey ${token}` };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const raw = queryInput.trim();
    if (!raw) {
      setErrorMessage(
        identifierType === 'email' ? 'Please enter an email address.' : 'Please enter an MD5 hash.'
      );
      setShowErrorDialog(true);
      return;
    }

    if (identifierType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(raw)) {
        setErrorMessage('Please enter a valid email address.');
        setShowErrorDialog(true);
        return;
      }
    }

    if (!selectedOrganization) {
      setErrorMessage('Please select an organization.');
      setShowErrorDialog(true);
      return;
    }

    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }

    setIsSubmitting(true);
    setResult(null);

    try {
      const headers = buildApiKeyHeaders();

      const encoded = encodeURIComponent(raw);
      const endpoint =
        identifierType === 'email'
          ? `/api/v2/consumer_profile_email/${encoded}`
          : `/api/v2/consumer_profile_md5/${encoded}`;

      const config = {
        params: { organization: selectedOrganization },
        ...(Object.keys(headers).length > 0 ? { headers } : {}),
      };

      const res = await api.get(endpoint, config);
      setResult(res.data);
      toast.success('Profile retrieved successfully');
    } catch (error) {
      console.error('Query error:', error);
      const statusCode = error.response?.status;
      const errorMsg =
        error.response?.data?.detail || error.message || 'Failed to retrieve profile. Please try again.';

      if (statusCode === 404) {
        if (identifierType === 'email') {
          setErrorMessage(
            `No profile found for email: ${raw}. Please verify the email address is correct.`
          );
        } else {
          setErrorMessage(`No profile found for MD5: ${raw}. Please verify the MD5 hash is correct.`);
        }
      } else {
        setErrorMessage(errorMsg);
      }

      setShowErrorDialog(true);
      setResult(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onIdentifierTypeChange = (value) => {
    setIdentifierType(value);
    setResult(null);
  };

  const curlEndpoint =
    identifierType === 'email'
      ? `/api/v2/consumer_profile_email/${encodeURIComponent(queryInput.trim())}`
      : `/api/v2/consumer_profile_md5/${encodeURIComponent(queryInput.trim())}`;

  return (
    <div className="pb-6 space-y-6">
      <PageHeaderActions>
        {organizations.length === 0 ? (
          <FunctionalSelect
            label="Organization"
            value=""
            onValueChange={() => {}}
            placeholder="No organizations"
            className={HEADER_SELECT_CLASS}
            disabled
          />
        ) : (
          <FunctionalSelect
            label="Organization"
            className={HEADER_SELECT_CLASS}
            value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
            onValueChange={(value) => {
              const parsed = organizationsService.parseOrganizationOptionValue(value);
              if (parsed) {
                setSelectedOrganization(parsed.name);
                organizationsService.setSelectedOrganization(parsed.name, 'name', [
                  { name: parsed.name, id: parsed.id },
                ]);
              } else {
                setSelectedOrganization(value);
                organizationsService.setSelectedOrganization(value, 'name', organizations);
              }
            }}
            placeholder="Select organization"
            disabled={isSubmitting}
          >
            {organizations.map((org) => (
              <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                {org.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        )}
      </PageHeaderActions>
      <Card>
        <CardHeader>
          <CardTitle>Consumer Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {organizations.length === 0 ? (
              <p className="text-sm text-amber-600">
                No organizations available. Please contact an administrator to be added to an organization.
              </p>
            ) : null}
            <div className="space-y-2">
              <FunctionalSelect
                label="Email type"
                id="identifierType"
                className="w-1/4"
                value={identifierType}
                onValueChange={onIdentifierTypeChange}
                placeholder="Select type"
                disabled={isSubmitting}
              >
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="md5">MD5</SelectItem>
              </FunctionalSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="queryInput">{identifierType === 'email' ? 'Email' : 'MD5'}</Label>
              <Input
                id="queryInput"
                type={identifierType === 'email' ? 'email' : 'text'}
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                placeholder={identifierType === 'email' ? 'Enter email address' : 'Enter MD5 hash'}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useApiKey"
                  checked={useApiKey}
                  onChange={(e) => {
                    setUseApiKey(e.target.checked);
                    if (!e.target.checked) {
                      setApiKeyInput('');
                    }
                  }}
                  disabled={isSubmitting}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="useApiKey" className="cursor-pointer">
                  Use API Key
                </Label>
              </div>
            </div>

            {useApiKey && (
              <div className="space-y-2">
                <Label htmlFor="apiKeyInput">API Key</Label>
                <Input
                  id="apiKeyInput"
                  type="password"
                  autoComplete="off"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Enter API key"
                  disabled={isSubmitting}
                />
              </div>
            )}

            <Button
              type="submit"
              variant="outline"
              disabled={isSubmitting || !selectedOrganization || (useApiKey && !apiKeyInput.trim())}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>

          {result && useApiKey && apiKeyInput.trim() && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Query:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {(() => {
                    const baseUrl = window.location.origin;
                    const apiKeyValue = '<API_KEY_TOKEN>';
                    const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
                    const isHttps = baseUrl.startsWith('https://');
                    const kFlag = isLocalhost && isHttps ? ' -k' : '';

                    return `curl -X GET "${baseUrl}${curlEndpoint}"${kFlag} \\\n  -H "Authorization: apikey ${apiKeyValue}"`;
                  })()}
                </pre>
              </div>
            </div>
          )}

          {result && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Result:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(formatProfileResultForDisplay(result), null, 2)}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default Query;
